import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './scss/paginated-options.scss';

const PaginatedOptions = ({ htmlContent }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    // Buscar la lista de opciones
    const textContent = doc.body.textContent || '';

    // Detectar si contiene "Opciones disponibles:"
    if (!textContent.includes('Opciones disponibles:')) {
      setParsedData(null);
      return;
    }

    const headerMatch = htmlContent.match(/(.*?)Opciones disponibles:/s);
    const header = headerMatch ? headerMatch[1].trim() : '';

    const footerMatch = htmlContent.match(/Responde con el número.*$/s);
    const footer = footerMatch ? footerMatch[0].trim() : '';

    // Extraer la sección de opciones (entre "Opciones disponibles:" y "Responde con el número...")
    const optionsSection = textContent.match(/Opciones disponibles:(.*?)(?:Responde con el número|$)/s);
    const optionsText = optionsSection ? optionsSection[1] : '';

    // Extraer las opciones numeradas solo de la sección de opciones
    const optionsRegex = /(\d+)\.\s+([^\n]+(?:\n(?!\d+\.|Responde)[^\n]+)*)/g;
    const options = [];
    let match;

    while ((match = optionsRegex.exec(optionsText)) !== null) {
      options.push({
        number: match[1],
        text: match[2].trim()
      });
    }

    if (options.length > 0) {
      setParsedData({
        header,
        options,
        footer,
        totalPages: Math.ceil(options.length / ITEMS_PER_PAGE)
      });
    } else {
      setParsedData(null);
    }
  }, [htmlContent]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    if (parsedData) {
      setCurrentPage((prev) => Math.min(parsedData.totalPages - 1, prev + 1));
    }
  };

  if (!parsedData) {
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  }

  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentOptions = parsedData.options.slice(startIndex, endIndex);
  const totalOptions = parsedData.options.length;

  const updatedFooter = parsedData.footer.replace(
    /\d+-\d+/,
    `${startIndex + 1}-${Math.min(endIndex, totalOptions)}`
  );

  // Función para formatear el texto con negritas en el nombre del producto
  const formatOptionText = (text) => {
    const match = text.match(/^([^\?\n]+)\?(.*)$/s);

    if (match) {
      const productName = match[1].trim();
      const restOfText = match[2].trim();

      return (
        <>
          <strong>{productName}</strong> {restOfText}
        </>
      );
    }

    return text;
  };

  return (
    <div className="paginated-options">
      {/* Encabezado */}
      <div dangerouslySetInnerHTML={{ __html: parsedData.header }} />

      {/* Opciones disponibles */}
      <div className="options-header">
        <strong>Opciones disponibles:</strong>
        <span className="page-indicator">
          Mostrando {startIndex + 1}-{Math.min(endIndex, totalOptions)} de {totalOptions}
        </span>
      </div>

      {/* Lista de opciones de la página actual */}
      <div className="options-list">
        {currentOptions.map((option) => (
          <div key={option.number} className="option-item">
            <span className="option-number">{option.number}.</span>
            <span className="option-text">{formatOptionText(option.text)}</span>
          </div>
        ))}
      </div>

      {/* Controles de paginación - solo mostrar si hay más de una página */}
      {parsedData.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className="pagination-btn"
            aria-label="Página anterior"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Anterior
          </button>

          <span className="page-info">
            Página {currentPage + 1} de {parsedData.totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage === parsedData.totalPages - 1}
            className="pagination-btn"
            aria-label="Página siguiente"
          >
            Siguiente
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}

      {/* Footer (instrucción para responder) */}
      <div className="options-footer" dangerouslySetInnerHTML={{ __html: updatedFooter }} />
    </div>
  );
};

export default PaginatedOptions;
